import _ from "lodash";
import React, { useState, useEffect } from "react";
import classNames from "classnames";
import classes from "./StaffList.scss";
import { useI18n } from "Hooks";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import {
  Avatar,
  SearchInput,
  EmptyState,
  Modal,
  Button,
  Checkbox,
} from "@toddle-edu/ds-web";
import { TODDLE_AI, USER } from "../../../modules/OrgAddOnContants";
import { updateAddOnStatus } from "../../../modules/OrgAddOnHelpers";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { updateStudentObject } from "StudentSetup/components/CreateStudent/containers/CreateStudentModule";
import { prepareLoadMoreFeed } from "Utils";
import { getTeacherFeedQuery } from "AddOnManager/modules/AddOnManagerQueries";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { AVATAR_COLOR } from "AttendanceDashboard/modules/Utils";

const styleObj = {
  modalBodyStyle: {
    padding: "0px",
    marginBottom: "0px",
  },
};

const StaffList = props => {
  const {
    staff,
    orgId,
    feature,
    isEdit = true,
    updateAddOnStatus,
    networkStatusStaff,
    setFullScreenLoader,
    curriculumProgramId,
    isStaffListModalOpen,
    setIsStaffListModalOpen,
    showHeaderContainer = true,
    selectedCurriculumEnabledUsers,
    getInternalToolGenericDataVariables,
    onSuccessAddOnConfigChange,
    addOnType = TODDLE_AI,
  } = props;
  const { t } = useI18n(["common"]);

  const staffList = _.get(staff, "edges", []);
  const showLoader = networkStatusStaff <= 3;

  const [staffSearchText, setStaffSearchText] = useState("");
  const [selectedStaffIds, setSelectedStaffIds] = useState(
    selectedCurriculumEnabledUsers
  );

  const [filteredStaffs, setFilteredStaffs] = useState(staffList);

  const getSortedUserList = userList => {
    return _.sortBy(userList, [
      user => !_.includes(selectedStaffIds, user?.node?.id),
      user => _.toLower(user?.node?.firstName),
      user => _.toLower(user?.node?.lastName),
    ]);
  };

  useEffect(() => {
    setFilteredStaffs(getSortedUserList(staffList));
  }, [networkStatusStaff]);

  useEffect(() => {
    setSelectedStaffIds(selectedCurriculumEnabledUsers);
  }, [selectedCurriculumEnabledUsers, isStaffListModalOpen]);

  useEffect(() => {
    handleSearch(staffSearchText);
  }, [staffSearchText]);

  const selectStaffHandler = ({ isStaffSelected, id }) => {
    if (isStaffSelected) {
      setSelectedStaffIds(
        _.filter(selectedStaffIds, staffId => staffId !== id)
      );
    } else {
      setSelectedStaffIds([...selectedStaffIds, id]);
    }
  };

  const handleSearch = searchText => {
    if (_.isEmpty(searchText)) {
      // if search text is empty all staff should be shown
      setFilteredStaffs(getSortedUserList(staffList));
    } else if (_.includes(searchText, ",")) {
      // if search text contains "," we will look for exact emails seperated by comma
      const emailsFromSearchText = _.map(_.split(searchText, ","), email =>
        _.toLower(_.trim(email))
      );
      const validEmailsFromSearchText = _.filter(
        emailsFromSearchText,
        email => !_.isEmpty(email)
      );
      const filteredStaffList = _.filter(staffList, staff =>
        _.includes(
          validEmailsFromSearchText,
          _.toLower(_.trim(staff?.node?.email))
        )
      );
      setFilteredStaffs(getSortedUserList(filteredStaffList));
    } else {
      // else will search for both either name or email partially match the search text
      const filteredStaffList = _.filter(
        staffList,
        staff =>
          _.includes(
            _.toLower(_.trim(staff?.node?.email)),
            _.toLower(_.trim(searchText))
          ) ||
          _.includes(
            _.toLower(
              `${_.trim(staff?.node?.firstName)} ${_.trim(
                staff?.node?.lastName
              )}`
            ),
            _.toLower(_.trim(searchText))
          )
      );
      setFilteredStaffs(getSortedUserList(filteredStaffList));
    }
  };

  const handleSelectAll = () => {
    const filteredStaffIds = _.map(filteredStaffs, staff => staff?.node?.id);
    const staffListIds = _.map(staffList, staff => staff?.node?.id);

    if (_.size(staffSearchText)) {
      // Select all searched staffs
      if (_.every(filteredStaffIds, id => _.includes(selectedStaffIds, id))) {
        // if already these staffs selected, unselect these staffs
        setSelectedStaffIds(_.difference(selectedStaffIds, filteredStaffIds));
      } else {
        // else select all who match search
        setSelectedStaffIds(_.union(selectedStaffIds, filteredStaffIds));
      }
    } else {
      // Select all existing staff
      if (_.isEqual(_.size(staffListIds), _.size(selectedStaffIds))) {
        // if already all select, unselect all
        setSelectedStaffIds([]);
      } else {
        // else select all
        setSelectedStaffIds(staffListIds);
      }
    }
  };

  const getSelectAllCheckboxStatus = () => {
    const filteredStaffIds = _.map(filteredStaffs, staff => staff?.node?.id);
    return _.every(filteredStaffIds, id => _.includes(selectedStaffIds, id));
  };

  const getSelectAllCheckboxIndeterminateStatus = () => {
    const filteredStaffIds = _.map(filteredStaffs, staff => staff?.node?.id);
    return (
      !getSelectAllCheckboxStatus() &&
      !_.isEmpty(_.intersection(selectedStaffIds, filteredStaffIds))
    );
  };

  const onModalClose = () => {
    setSelectedStaffIds([]);
    setIsStaffListModalOpen(false);
  };

  const staffListContainerClasses = classNames({
    [classes.staffListContainer]: true,
    [classes.staffListContainerHeight]: !showHeaderContainer,
  });
  const searchContainerClasses = classNames({
    [classes.editSearchContainer]: isEdit,
    [classes.searchContainer]: !isEdit,
    [classes.searchContainerPadding]: !showHeaderContainer,
  });
  const listContainerClasses = classNames({
    [classes.editListContainer]: isEdit,
    [classes.listContainer]: !isEdit,
    [classes.listContainerPadding]: !showHeaderContainer,
  });
  const rowContainerClasses = classNames({
    [classes.rowContainer]: true,
    [classes.rowContainerPadding]: !showHeaderContainer,
    [classes.rowContainerHover]: !showHeaderContainer,
  });

  const handleSave = async () => {
    setFullScreenLoader(true);
    const enabledUserIds = _.difference(
      selectedStaffIds,
      selectedCurriculumEnabledUsers
    );
    const disabledUserIds = _.difference(
      selectedCurriculumEnabledUsers,
      selectedStaffIds
    );
    await updateAddOnStatus({
      orgId,
      updateOn: USER,
      enabledUserIds,
      disabledUserIds,
      addOnType,
      moduleId: feature?.id,
      curriculumId: curriculumProgramId,
      queryVariables: getInternalToolGenericDataVariables,
    });
    if (!_.isNil(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    onModalClose();
    setFullScreenLoader(false);
  };

  return (
    <Modal
      width="544px"
      height="80vh"
      hasOverlay={true}
      onClose={onModalClose}
      shouldCloseOnEsc={true}
      isOpen={isStaffListModalOpen}
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Header
        title={t("common:select_staff")}
        isClosable={true}
        hasSeparator={true}
      />
      <Modal.Body isScrollable={true} style={styleObj.modalBodyStyle}>
        <div className={staffListContainerClasses}>
          <div className={searchContainerClasses}>
            <SearchInput
              size={"medium"}
              value={staffSearchText}
              onChange={setStaffSearchText}
              placeholder={"Search by name, email or comma seperated emails"}
            />
          </div>
          {_.isEmpty(filteredStaffs) && !showLoader ? (
            <EmptyState illustration={NoSearchResultsIllustration} />
          ) : (
            <div className={listContainerClasses}>
              <div className={classes.allOption}>
                {_.size(staffSearchText)
                  ? "Select all matching staff"
                  : "Select all staff"}
                <Checkbox
                  _legacy_size={"x-small"}
                  isChecked={getSelectAllCheckboxStatus()}
                  onChange={handleSelectAll}
                  isIndeterminate={getSelectAllCheckboxIndeterminateStatus()}
                />
              </div>
              {_.map(filteredStaffs, ({ node }, index) => {
                const avatarColor = AVATAR_COLOR[index % _.size(AVATAR_COLOR)];
                const isStaffSelected = _.includes(selectedStaffIds, node?.id);
                return (
                  <div
                    key={node?.id}
                    className={rowContainerClasses}
                    onClick={() =>
                      selectStaffHandler({
                        isStaffSelected,
                        id: node?.id,
                      })
                    }
                  >
                    <div className={classes.rowSubContainer}>
                      <div className={classes.leftContainer}>
                        <Avatar
                          name={`${node?.firstName} ${node?.lastName}`}
                          src={_.get(node, "profileImage")}
                          color={avatarColor}
                          size="medium"
                          shade="light"
                        />

                        <div className={classes.teacherNameContainer}>
                          <div className={classes.teacherName}>
                            {node?.firstName + " " + node?.lastName}
                          </div>
                          <div className={classes.teacherEmail}>
                            {node?.email}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Checkbox
                      _legacy_size={"x-small"}
                      isChecked={isStaffSelected}
                      onChange={() =>
                        selectStaffHandler({
                          isStaffSelected,
                          id: node?.id,
                        })
                      }
                    />
                  </div>
                );
              })}
              {showLoader && (
                <div className={classes.loadingContainer}>
                  <Loading />
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.footerContainer}>
          <div className={classes.selectedCountText}>
            {`Total ${_.size(selectedStaffIds)} staff selected`}
          </div>
          <div className={classes.buttonContainer}>
            <div className={classes.button}>
              <Button
                onClick={onModalClose}
                _legacy_variant={"outlined-subtle"}
                variant={"neutral"}
                type={"outlined"}
                size={"medium"}
              >
                {t("common:cancel")}
              </Button>
            </div>
            <div className={classes.button}>
              <Button
                onClick={handleSave}
                variant={"progressive"}
                size={"medium"}
              >
                {t("common:save")}
              </Button>
            </div>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapActionCreators = {
  setToastMsg,
  updateStudentObject,
  updateAddOnStatus,
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(ownProps, "orgId");
  const curriculumProgramId = _.get(ownProps, "selectedCurriculumId");

  const teacherFilters = {
    courses: [],
    curriculumProgramIds: [curriculumProgramId],
  };
  return {
    organizationId,
    teacherFilters,
    curriculumProgramId,
    orderByDirection: "ASC",
    orderBy: "FIRST_NAME",
    isData: true,
    isLoading: false,
  };
};

export default compose(
  I18nHOC({ resource: ["schoolSetup", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getTeacherFeedQuery, {
    name: "getTeacherFeed",
    skip: ({ curriculumProgramId }) => !curriculumProgramId,
    options: ({
      organizationId,
      teacherFilters,
      orderBy,
      orderByDirection,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filters: teacherFilters,
        orderByDirection,
        orderBy,
      },
    }),
    props: ({
      getTeacherFeed,
      ownProps: {
        isData,
        isLoading,
        organizationId,
        teacherFilters,
        orderByDirection,
        orderBy,
      },
    }) => {
      const queryData = _.get(getTeacherFeed, "node", {});

      return {
        staff: _.get(queryData, `staff`, {}),
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getTeacherFeed["networkStatus"]) || isLoading,
        networkStatusStaff: getTeacherFeed["networkStatus"],
        loadMoreTeachers: prepareLoadMoreFeed({
          variables: {
            organizationId,
            after: _.get(getTeacherFeed, "node.staff.pageInfo.endCursor"),
            filters: teacherFilters,
            orderByDirection,
            orderBy,
          },
          queryData: getTeacherFeed,
          key: "staff",
        }),
      };
    },
  })
)(StaffList);
